/**
 * A tiny "bridge" layer for subscription purchases.
 *
 * - In a normal web browser: show a placeholder popup and do nothing else.
 * - In a wrapped Capacitor app: call a native purchase entrypoint (stubbed for now).
 *
 * This file intentionally does NOT connect to any payment system by itself.
 */

function getCapacitorGlobal() {
  // Capacitor injects a global "Capacitor" object in wrapped builds.
  // We avoid importing @capacitor/core so the web build does not require it.
  return typeof window !== 'undefined' ? window.Capacitor : undefined;
}

export function isRunningInCapacitor() {
  const cap = getCapacitorGlobal();
  if (!cap) return false;

  // Newer Capacitor versions expose isNativePlatform(). Fallback to platform !== 'web'.
  if (typeof cap.isNativePlatform === 'function') return cap.isNativePlatform();
  if (typeof cap.getPlatform === 'function') return cap.getPlatform() !== 'web';
  if (typeof cap.platform === 'string') return cap.platform !== 'web';

  return false;
}

function showWebPlaceholder() {
  window.alert(
    'Payment will be processed through the App Store / Google Play after you install the mobile app',
  );
}

async function nativeSubscribe({ tierId }) {
  const cap = getCapacitorGlobal();

  // Connection point: in the wrapped app, you’ll add a real plugin here later, e.g.
  // - RevenueCat / Capacitor Purchases plugin
  // - StoreKit / BillingClient via a custom Capacitor plugin
  //
  // For now, do NOT charge money and do NOT connect to any payment system.
  const plugin = cap?.Plugins?.StoreBridge;
  if (plugin?.subscribe) {
    return await plugin.subscribe({ tierId });
  }

  // Stubbed behavior: no-op (invisible to user) besides a console log.
  // This ensures we have a "bridge" to hook into later without changing UI code.
  // eslint-disable-next-line no-console
  console.log('[subscriptionBridge] Native subscribe stub called', { tierId });
  return { ok: false, reason: 'STORE_BRIDGE_NOT_IMPLEMENTED' };
}

export async function subscribeToTier(tierId) {
  if (!isRunningInCapacitor()) {
    showWebPlaceholder();
    return { ok: false, reason: 'WEB_PLACEHOLDER' };
  }

  return await nativeSubscribe({ tierId });
}

